/**
 * El desglose de un gasto en varias categorías.
 *
 * ---------------------------------------------------------------------------
 *  Para qué sirve
 * ---------------------------------------------------------------------------
 *
 * Una compra en la tienda del barrio casi nunca es de una sola cosa: son 14.000 de
 * mercado, 4.000 de mecato y 7.000 de la recarga del celular, todo apuntado en la
 * libreta como una sola deuda. Sin desglose, ese gasto entero cae en una categoría y
 * los informes mienten: el mercado parece más caro y el gasto hormiga desaparece.
 *
 * Lo tenía Cuentia («compra fiado multi-ítem») y se había quedado fuera. Aquí sirve
 * además para cualquier gasto, no solo para el fiado.
 *
 * ---------------------------------------------------------------------------
 *  La cuenta que se hace sola
 * ---------------------------------------------------------------------------
 *
 * Mientras se escriben las líneas, se compara su suma con el total. Si no cuadra, se
 * dice cuánto falta o cuánto sobra **antes** de guardar. Es lo mismo que hace la
 * pantalla de recibos, y es donde más se equivoca uno a mano.
 *
 * Y se puede hacer en los dos sentidos: quien ya sabe el total lo escribe y reparte;
 * quien va leyendo el recibo escribe las líneas y el total se calcula.
 */

import { Plus, Trash2, TriangleAlert, Check } from 'lucide-react';

import { formatCOP, parseAmountFromText } from '@eldescuadre/core';

import { Money } from './ui';

/** Una línea mientras se está escribiendo. */
export interface DraftItem {
  id: string;
  description: string;
  amountText: string;
  categoryId: string;
}

/** Una línea vacía. */
export function emptyItem(): DraftItem {
  return {
    // Se genera aquí y no con el índice: si se usa el índice como clave, al borrar una
    // línea del medio React reutiliza los campos de la siguiente y el texto salta.
    id: crypto.randomUUID(),
    description: '',
    amountText: '',
    categoryId: '',
  };
}

/** Lo que suman las líneas escritas. */
export function sumItems(items: DraftItem[]): number {
  return items.reduce((acc, item) => acc + (parseAmountFromText(item.amountText) ?? 0), 0);
}

/** Las líneas que tienen algo escrito, listas para mandar al servidor. */
export function filledItems(
  items: DraftItem[],
): Array<{ description: string; amount: number; categoryId?: string | null }> {
  return items
    .filter((item) => item.description.trim() && (parseAmountFromText(item.amountText) ?? 0) > 0)
    .map((item) => ({
      description: item.description.trim(),
      amount: parseAmountFromText(item.amountText) ?? 0,
      categoryId: item.categoryId || null,
    }));
}

export function ItemBreakdown({
  items,
  onChange,
  categories,
  total,
  onUseSum,
  totalLabel = 'Total',
}: {
  items: DraftItem[];
  onChange: (items: DraftItem[]) => void;
  categories: ReadonlyArray<{ id: string; name: string }>;
  /** El total del movimiento, para comparar. */
  total: number;
  /** Cuando se pulsa «usar esta suma»: pone el total a partir de las líneas. */
  onUseSum?: ((sum: number) => void) | undefined;
  totalLabel?: string;
}): React.JSX.Element {
  const sum = sumItems(items);
  const difference = total - sum;
  const matches = total > 0 && difference === 0;
  const hasLines = items.some((item) => (parseAmountFromText(item.amountText) ?? 0) > 0);

  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={item.id} className="flex flex-wrap gap-2 sm:flex-nowrap">
            <input
              className="ed-input min-w-0 flex-1"
              value={item.description}
              onChange={(e) =>
                onChange(
                  items.map((row) =>
                    row.id === item.id ? { ...row, description: e.target.value } : row,
                  ),
                )
              }
              placeholder={`Qué fue (línea ${index + 1})`}
              aria-label={`Descripción de la línea ${index + 1}`}
              autoComplete="off"
            />

            <select
              className="ed-input !w-auto min-w-[8rem] flex-1"
              value={item.categoryId}
              onChange={(e) =>
                onChange(
                  items.map((row) =>
                    row.id === item.id ? { ...row, categoryId: e.target.value } : row,
                  ),
                )
              }
              aria-label={`Categoría de la línea ${index + 1}`}
            >
              <option value="">Sin categoría</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            <input
              className="ed-input ed-money !w-24 shrink-0"
              inputMode="numeric"
              value={item.amountText}
              onChange={(e) =>
                onChange(
                  items.map((row) =>
                    row.id === item.id ? { ...row, amountText: e.target.value } : row,
                  ),
                )
              }
              placeholder="0"
              aria-label={`Importe de la línea ${index + 1}`}
              autoComplete="off"
            />

            <button
              type="button"
              className="ed-btn-ghost !w-auto shrink-0 !px-2"
              onClick={() => onChange(items.filter((row) => row.id !== item.id))}
              disabled={items.length === 1}
              aria-label={`Quitar la línea ${index + 1}`}
            >
              <Trash2 size={15} aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="ed-btn-ghost w-full text-xs"
        onClick={() => onChange([...items, emptyItem()])}
      >
        <Plus size={14} aria-hidden="true" />
        Añadir una línea
      </button>

      {/* La cuenta. Solo aparece cuando hay algo que comparar. */}
      {hasLines && (
        <div
          className={`rounded-ed border p-3 text-sm ${
            matches
              ? 'border-ed-success/40 bg-ed-success/10'
              : 'border-ed-gold/40 bg-ed-gold/10'
          }`}
        >
          <div className="flex justify-between">
            <span className="text-ed-text-muted">Suma de las líneas</span>
            <Money amount={sum} className="font-semibold" />
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-ed-text-muted">{totalLabel}</span>
            <Money amount={total} className="font-semibold" />
          </div>

          <div className="mt-2 border-t border-ed-bg-border/30 pt-2">
            {matches ? (
              <p className="flex items-center gap-1.5 text-ed-success">
                <Check size={14} aria-hidden="true" />
                Cuadra: las líneas suman exactamente el total.
              </p>
            ) : difference > 0 ? (
              <p className="flex items-center gap-1.5 text-ed-gold">
                <TriangleAlert size={14} aria-hidden="true" />
                <span>
                  Faltan <Money amount={difference} /> por repartir.
                </span>
              </p>
            ) : (
              <p className="flex items-center gap-1.5 text-ed-coral-light">
                <TriangleAlert size={14} aria-hidden="true" />
                <span>
                  Las líneas se pasan por <Money amount={Math.abs(difference)} />.
                </span>
              </p>
            )}

            {!matches && onUseSum && sum > 0 && (
              <button
                type="button"
                className="ed-btn-ghost mt-2 !min-h-[34px] w-full text-xs"
                onClick={() => onUseSum(sum)}
              >
                Poner el total en {formatCOP(sum)}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
